import React from 'react';
import { formatRelativeTime as formatRelativeTimeShared, activeTimezone } from '../../utils/formatDateTime';
import { useTimezone } from '../../contexts/TimezoneContext';

// THUB-272 / THUB-186: delegate to the single shared, timezone-aware formatter.
// These replace local copies that parsed the backend's (formerly naive) timestamps as
// LOCAL time -- which future-dated every value and made every recent row render
// "Just now". Module-level shims rather than the useTimezone() hook because these are
// called from several sub-components and inside .map callbacks in this file.
function formatRelativeTime(value: string | null | undefined): string {
  return formatRelativeTimeShared(value, activeTimezone());
}

/**
 * Item that needs operator attention
 */
export interface AttentionItem {
  id: string;
  type: 'connection' | 'task' | 'partner';
  name: string;
  partnerName?: string;
  issue: string;
  since?: string;
  severity: 'warning' | 'error';
}

export interface NeedsAttentionPanelProps {
  /** Items requiring attention */
  items: AttentionItem[];
  /** Loading state */
  isLoading?: boolean;
  /** Click handler for item */
  onItemClick?: (item: AttentionItem) => void;
  /** Maximum items to display */
  maxItems?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Shimmer skeleton for loading state
 */
const shimmerClasses =
  'animate-shimmer bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 bg-[length:200%_100%] dark:from-slate-700 dark:via-slate-600 dark:to-slate-700';

/**
 * Get icon for item type
 */
const TypeIcon: React.FC<{ type: AttentionItem['type']; className?: string }> = ({ type, className = '' }) => {
  switch (type) {
    case 'connection':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      );
    case 'task':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    case 'partner':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
  }
};

/**
 * Format relative time (e.g., "15 min ago")
 */
/**
 * Loading skeleton
 */
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
        <div className={`${shimmerClasses} w-8 h-8 rounded`} />
        <div className="flex-1 space-y-2">
          <div className={`${shimmerClasses} h-4 w-32 rounded`} />
          <div className={`${shimmerClasses} h-3 w-48 rounded`} />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Empty state when no issues
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <svg
      className="w-12 h-12 text-emerald-500 dark:text-emerald-400 mb-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <p className="text-sm font-medium text-neutral-600 dark:text-slate-300">All Clear</p>
    <p className="text-xs text-neutral-500 dark:text-slate-400 mt-1">No issues requiring attention</p>
  </div>
);

/**
 * NeedsAttentionPanel - Shows items requiring operator attention
 *
 * Replaces TaskExecutionSummary with clearer, actionable information:
 * - Failed connections (with error reason)
 * - Failed tasks (with error message)
 * - Partners with issues
 *
 * Shows "All Clear" when empty - this is good news!
 */
export const NeedsAttentionPanel: React.FC<NeedsAttentionPanelProps> = ({
  items,
  isLoading = false,
  onItemClick,
  maxItems = 5,
  className = '',
}) => {
  // THUB-186: subscribe to the timezone context so this component RE-RENDERS when the
  // selector changes. The formatter shims above read a module cache, which returns the
  // right value but cannot itself trigger a render -- without this the timestamps kept
  // showing the previous zone until a full page reload.
  useTimezone();
  const displayItems = items.slice(0, maxItems);
  const hasMore = items.length > maxItems;

  return (
    <div
      className={`
        bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden
        dark:bg-slate-800 dark:border-slate-700
        ${className}
      `}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-neutral-100 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wide">
            Needs Attention
          </h3>
          {items.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              {items.length}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isLoading ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {displayItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onItemClick?.(item)}
                className={`
                  w-full flex items-start gap-3 p-3 rounded-lg text-left
                  transition-colors duration-150
                  ${item.severity === 'error'
                    ? 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                    : 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
                  }
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  dark:focus:ring-offset-slate-800
                `}
              >
                {/* Severity indicator */}
                <div
                  className={`
                    flex-shrink-0 p-1.5 rounded
                    ${item.severity === 'error'
                      ? 'bg-red-100 dark:bg-red-900/40'
                      : 'bg-amber-100 dark:bg-amber-900/40'
                    }
                  `}
                >
                  <TypeIcon
                    type={item.type}
                    className={`w-4 h-4 ${
                      item.severity === 'error'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-amber-600 dark:text-amber-400'
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase">
                      {item.type}:
                    </span>
                    <span className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {item.name}
                    </span>
                  </div>
                  {item.partnerName && (
                    <p className="text-xs text-neutral-500 dark:text-slate-400 truncate">
                      {item.partnerName}
                    </p>
                  )}
                  <p
                    className={`text-xs mt-1 ${
                      item.severity === 'error'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-amber-600 dark:text-amber-400'
                    }`}
                  >
                    {item.issue}
                  </p>
                  {item.since && (
                    <p className="text-xs text-neutral-400 dark:text-slate-500 mt-1">
                      Since: {formatRelativeTime(item.since)}
                    </p>
                  )}
                </div>

                {/* Arrow indicator */}
                <svg
                  className="w-4 h-4 text-neutral-400 dark:text-slate-500 flex-shrink-0 mt-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}

            {hasMore && (
              <p className="text-xs text-center text-neutral-500 dark:text-slate-400 pt-2">
                +{items.length - maxItems} more items
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedsAttentionPanel;
